import { useForm } from 'react-hook-form'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import api from '@/lib/axios'

export default function Profile() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { register: registerProfile, handleSubmit: handleProfile } = useForm({
    defaultValues: { full_name: user?.full_name || '' },
  })
  const { register: registerPwd, handleSubmit: handlePwd, reset: resetPwd } = useForm<{
    current_password: string; new_password: string
  }>()

  const updateProfileMutation = useMutation({
    mutationFn: (data: { full_name: string }) => api.patch('/users/me', data).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  })

  const updatePasswordMutation = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      api.post('/users/me/password', data).then((r) => r.data),
    onSuccess: () => resetPwd(),
  })

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold">Profile Settings</h1>

      <Card>
        <CardHeader><CardTitle>Personal Information</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleProfile((d) => updateProfileMutation.mutate(d))} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email} disabled />
            </div>
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input {...registerProfile('full_name')} />
            </div>
            <Button type="submit" disabled={updateProfileMutation.isPending}>
              {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Change Password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handlePwd((d) => updatePasswordMutation.mutate(d))} className="space-y-4">
            {updatePasswordMutation.error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                Current password is incorrect
              </div>
            )}
            {updatePasswordMutation.isSuccess && (
              <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
                Password updated successfully
              </div>
            )}
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input type="password" {...registerPwd('current_password')} />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" {...registerPwd('new_password')} />
            </div>
            <Button type="submit" disabled={updatePasswordMutation.isPending}>
              {updatePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
